import { BodySmall, DropdownSearch, DropdownSearchItem, SubtleButton, theme, Tooltip } from '@arnold/common';
import styled from '@emotion/styled';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeamioIcon } from '../../assets/images';
import {
  ContactSource,
  ContactType,
  Features,
  OrganizationTeamsQuery,
  RespondentOriginator,
} from '../../generated/hooks';
import { useFeatureFlag } from '../../lib/helpers/hooks';
import { CollapsibleContainer } from './CollapsibleContainer';

export type TeamWithSubTeams = NonNullable<OrganizationTeamsQuery['organizationTeams']>[0] & {
  subTeams: TeamsWithSubTeams;
  numberOfEmployees: number;
  members: MemberWithLeaderOfTeams[];
};
export type TeamsWithSubTeams = TeamWithSubTeams[];

export type MemberWithLeaderOfTeams = NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['members'][0] & {
  leaderOfTeams: TeamsWithSubTeams;
};

interface Props {
  teams: TeamsWithSubTeams;
  organizationName: string;
  updateRespondent: (member: MemberWithLeaderOfTeams | null, teamId?: string) => void;
  createTeam: (supervisorTeamId: string) => void;
  updateTeam: (team: TeamWithSubTeams | null) => void;
  reportRecipients?: Map<string, string[]>;
}

export const TeamsAndPeople = ({
  teams,
  organizationName,
  updateRespondent,
  createTeam,
  updateTeam,
  reportRecipients,
}: Props) => {
  const [t] = useTranslation(['teamsScreen', 'peopleChart']);
  const [expandedTeamsIds, setExpandedTeamsIds] = useState<string[]>([]);
  const [searchedItem, setSearchedItem] = useState<TeamsSearchItem>(undefined);
  const idsOfAllTeams = getIdsOfAllTeamsAndSubTeams(teams);
  const isEveryTeamExpanded = expandedTeamsIds.length === idsOfAllTeams.length;
  const totalEmployees = teams.reduce((acc, team) => acc + team.numberOfEmployees, 0);
  const isEmployeeManagementDisabled = useFeatureFlag(Features.DisableEmployeeManagement);

  const contextItems = [
    {
      label: t('peopleChart:createTeam'),
      action: () => createTeam(''),
      dataCy: 'create-team',
    },
    {
      label: isEveryTeamExpanded ? t('collapseAll') : t('expandAll'),
      action: () => (isEveryTeamExpanded ? setExpandedTeamsIds([]) : setExpandedTeamsIds(idsOfAllTeams)),
      dataCy: isEveryTeamExpanded ? 'collapse-all' : 'expand-all',
    },
  ];

  const searchableItems = useMemo(() => {
    const flatTeamsArray = flatTeams(teams);
    return [
      ...flatTeamsArray.flatMap((team) =>
        team.members.map((member) => ({
          title: `${member.firstname} ${member.surname}`,
          searchables: [
            `${member.firstname} ${member.surname}`,
            ...(sortContacts(member.contacts).map((contact) => contact.value) || []),
            `${t('team')} ${team.name}`,
          ],
          additionalData: { type: 'member', id: member.id },
        })),
      ),
      ...flatTeamsArray.map((team) => ({
        title: team.name,
        description: t('settings:team'),
        searchables: [team.name],
        additionalData: { type: 'team', id: team.id },
      })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams]) as DropdownSearchItem<{ type: 'member' | 'team'; id: string }>[];

  const totalTeams = countAllTeams(teams);

  return (
    <>
      <ButtonAndSearchWrapper>
        <AddEmployeeAndTeamButtonsWrapper>
          {!isEmployeeManagementDisabled && (
            <>
              <SubtleButton onClick={() => createTeam('')} data-cy="create-team-button" data-icom="create-team-button">
                {t('peopleChart:addTeam')}
              </SubtleButton>
              <SubtleButton
                onClick={() => updateRespondent(null, undefined)}
                data-cy="add-employee-button"
                data-icom="add-employee-button"
              >
                {t('addEmployee')}
              </SubtleButton>
            </>
          )}
        </AddEmployeeAndTeamButtonsWrapper>
        <DropdownSearch<{ type: 'member' | 'team'; id: string }>
          placeholder={t('searchPlaceholder')}
          searchableItems={searchableItems}
          selectedItem={searchedItem}
          onSelectedItemChange={setSearchedItem}
          className="search"
          dataIcom="employees-search-input"
          noResultsText={t('common:noResults')}
        />
      </ButtonAndSearchWrapper>
      <LabelWrapper>
        <BodySmall color={theme.colors.text.secondary}>{t('teamsAndPeople')}</BodySmall>
        <BodySmall color={theme.colors.text.secondary}>{t('reports')}</BodySmall>
      </LabelWrapper>
      <CollapsibleContainer
        title={organizationName}
        headerItems={[
          {
            title: t('onboarding:subTeams', {
              postProcess: 'interval',
              count: totalTeams,
            }),
            dataCy: 'team-subteams',
          },
          { title: `${totalEmployees} ${t('people')}`, dataCy: 'team-members' },
        ]}
        expanded
        contextItems={isEmployeeManagementDisabled ? [] : contextItems}
      >
        {teams.map((team) => (
          <Team
            key={team.id}
            team={team}
            expandedTeamsIds={expandedTeamsIds}
            setExpandedTeamsIds={setExpandedTeamsIds}
            updateRespondent={updateRespondent}
            createTeam={createTeam}
            updateTeam={updateTeam}
            searchedItem={searchedItem}
            reportRecipients={reportRecipients}
          />
        ))}
      </CollapsibleContainer>
    </>
  );
};

interface TeamProps {
  team: TeamWithSubTeams;
  expandedTeamsIds: string[];
  setExpandedTeamsIds: React.Dispatch<React.SetStateAction<string[]>>;
  updateRespondent: (member: MemberWithLeaderOfTeams | null, teamId?: string) => void;
  createTeam: (supervisorTeamId: string) => void;
  updateTeam: (team: TeamWithSubTeams | null) => void;
  searchedItem?: TeamsSearchItem;
  reportRecipients?: Map<string, string[]>;
}

const Team = ({
  team,
  expandedTeamsIds,
  setExpandedTeamsIds,
  updateRespondent,
  createTeam,
  updateTeam,
  searchedItem,
  reportRecipients,
}: TeamProps) => {
  const [t] = useTranslation(['teamsScreen', 'peopleChart']);
  const ref = useRef<HTMLDivElement>(null);
  const highlighted = searchedItem?.additionalData.type === 'team' && searchedItem?.additionalData.id === team.id;
  const isEmployeeManagementDisabled = useFeatureFlag(Features.DisableEmployeeManagement);

  useEffect(() => {
    if (highlighted && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlighted]);

  const expanded = useMemo(() => {
    if (expandedTeamsIds.includes(team.id)) return true;
    if (
      searchedItem &&
      containsIdOfMemberOrTeam(team, searchedItem?.additionalData.id, searchedItem?.additionalData.type)
    ) {
      return true;
    }
    return false;
  }, [expandedTeamsIds, searchedItem, team]);

  const contextItems = team.structureId
    ? [
        { label: t('peopleChart:editTeam'), action: () => updateTeam(team), dataCy: 'edit-team' },
        { label: t('peopleChart:createTeam'), action: () => createTeam(team.id), dataCy: 'create-team' },
      ]
    : [];
  contextItems.push({
    label: t('peopleChart:addEmployee'),
    action: () => updateRespondent(null, team.id),
    dataCy: 'add-employee',
  });

  const totalSubTeams = countAllTeams(team.subTeams);

  return (
    <CollapsibleContainer
      title={team.name || t('teams:withoutLeader')}
      headerItems={
        [
          totalSubTeams > 0
            ? { title: t('onboarding:subTeams', { postProcess: 'interval', count: totalSubTeams }) }
            : null,
          { title: `${team.numberOfEmployees} ${t('people')}`, dataCy: 'team-members' },
          team.leader ? { title: `${team.leader.firstname} ${team.leader.surname}`, dataCy: 'team-leader' } : null,
        ].filter(Boolean) as { title: string; dataCy?: string }[]
      }
      expanded={expanded}
      onExpandClicked={() => setExpandedTeamsIds([...expandedTeamsIds, team.id])}
      onCollapseClicked={() => setExpandedTeamsIds(expandedTeamsIds.filter((id) => id !== team.id))}
      contextItems={isEmployeeManagementDisabled ? [] : contextItems}
      ref={ref}
      highlightTitle={highlighted}
      dataCy="team-table"
      dataIcom="employees-team"
      onRowClick={isEmployeeManagementDisabled ? undefined : team.structureId ? () => updateTeam(team) : undefined}
    >
      {team.members.map((member) => (
        <Person
          key={member.id}
          member={member as MemberWithLeaderOfTeams}
          updateRespondent={updateRespondent}
          searchedItem={searchedItem}
          reportRecipients={reportRecipients}
        />
      ))}
      {team.subTeams.map((subTeam) => (
        <Team
          key={subTeam.id}
          team={subTeam}
          expandedTeamsIds={expandedTeamsIds}
          setExpandedTeamsIds={setExpandedTeamsIds}
          updateRespondent={updateRespondent}
          createTeam={createTeam}
          updateTeam={updateTeam}
          searchedItem={searchedItem}
          reportRecipients={reportRecipients}
        />
      ))}
    </CollapsibleContainer>
  );
};

const countAllTeams = (teams: TeamsWithSubTeams): number => {
  return teams.reduce((acc, team) => {
    return acc + 1 + countAllTeams(team.subTeams);
  }, 0);
};

interface PersonProps {
  member: MemberWithLeaderOfTeams;
  updateRespondent: (member: MemberWithLeaderOfTeams | null, teamId?: string) => void;
  searchedItem?: TeamsSearchItem;
  reportRecipients?: Map<string, string[]>;
}

const Person = ({ member, updateRespondent, searchedItem, reportRecipients }: PersonProps) => {
  const [t] = useTranslation('teams');
  const ref = useRef<HTMLDivElement>(null);
  const highlighted = searchedItem?.additionalData.type === 'member' && searchedItem?.additionalData.id === member.id;

  // Get report recipient teams for this member from the Map
  const recipientTeams = reportRecipients?.get(member.id) || [];
  const allTeamsLabel = t('updateRespondent:allTeams');
  // if there is already "all teams" label, we do not want to show other team names
  const displayedTeamNames = recipientTeams.includes(allTeamsLabel)
    ? [allTeamsLabel]
    : [...new Set([...member.leaderOfTeams.map((t) => t.name), ...recipientTeams])];

  useEffect(() => {
    if (highlighted && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlighted]);

  return (
    <PersonContainer
      ref={ref}
      onClick={() => updateRespondent(member)}
      data-cy="team-member-row"
      data-icom="employees-person"
    >
      <PersonName
        data-icom="employees-person-name"
        highlighted={highlighted}
      >{`${member.firstname} ${member.surname}`}</PersonName>
      <LeaderOfTeams data-icom="employees-person-leader-of-teams" data-cy="report-for-teams">
        {displayedTeamNames.map((teamName, index, array) => {
          if (array.length > 3 && index === 2) {
            return (
              <LeaderOfTeam key={index}>
                +{array.length - 2} {t('more')}
              </LeaderOfTeam>
            );
          }
          if (array.length > 3 && index > 2) {
            return null;
          }
          return <LeaderOfTeam key={index}>{teamName}</LeaderOfTeam>;
        })}
      </LeaderOfTeams>
      {member.originator === RespondentOriginator.Teamio && (
        <Tooltip
          title={t('updateRespondent:addedAtTooltip', {
            date: (member?.createdAt && format(new Date(member.createdAt), 'Pp')) || '',
          })}
        >
          <img src={TeamioIcon} alt="Imported from Teamio" data-icom="employees-teamio-icon" />
        </Tooltip>
      )}
    </PersonContainer>
  );
};

const AddEmployeeAndTeamButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.f};
`;

const PersonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 16px;
  cursor: pointer;
  &:first-child {
    margin-top: ${theme.spacing.c};
  }
  &:hover {
    background-color: ${theme.colors.backgroundBasic.default};
  }
`;

const PersonName = styled.div<{ highlighted?: boolean }>`
  ${({ highlighted }) =>
    highlighted &&
    `
    background-color: ${theme.colors.emotionWarning.background};
    border-radius:  ${theme.borderRadius.s};
    outline: 1px solid ${theme.colors.emotionWarning.default};
    padding: 0 ${theme.spacing.d};
    line-height: 25px;
    margin-left: -${theme.spacing.c};
  `}
`;

const LeaderOfTeams = styled.div`
  flex-basis: 50%;
  display: flex;
  gap: 8px;
`;

const LeaderOfTeam = styled.div`
  background-color: ${theme.colors.backgroundBasic.default};
  padding: 2px 8px;
  border-radius: ${theme.borderRadius.s};
  font-size: ${theme.typography.body.small.regular.fontSize};
  color: ${theme.colors.text.primary};
`;

const getIdsOfAllTeamsAndSubTeams = (teams: TeamsWithSubTeams): string[] => {
  return teams.reduce((acc, team) => {
    return [...acc, team.id, ...getIdsOfAllTeamsAndSubTeams(team.subTeams)];
  }, [] as string[]);
};

export const flatTeams = (teams: TeamsWithSubTeams): TeamsWithSubTeams => {
  return teams.reduce((acc, team) => {
    return [...acc, team, ...flatTeams(team.subTeams)];
  }, [] as TeamsWithSubTeams);
};

const containsIdOfMemberOrTeam = (team: TeamsWithSubTeams[0], id: string, type: 'team' | 'member'): boolean => {
  if (type === 'team') {
    return team.id === id || team.subTeams.some((subTeam) => containsIdOfMemberOrTeam(subTeam, id, type));
  }
  return (
    team.members.some((member) => member.id === id) ||
    team.subTeams.some((subTeam) => containsIdOfMemberOrTeam(subTeam, id, type))
  );
};

const sortContacts = (
  contacts: NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['members'][0]['contacts'],
) => {
  if (!contacts) return [];
  return (
    [...contacts].sort((a, b) => {
      if (a.type === ContactType.Email && a.source === ContactSource.Primary) return -1;
      if (b.type === ContactType.Email && a.source === ContactSource.Primary) return 1;
      return 0;
    }) ?? []
  );
};

const ButtonAndSearchWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.i};

  .search {
    width: 320px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing.f};
    & > * {
      width: 100% !important;
    }
  }
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  & > * {
    flex-basis: 50%;
  }
`;

type TeamsSearchItem = DropdownSearchItem<{ type: 'member' | 'team'; id: string }> | undefined;
